import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Badge from '../../../components/base/Badge';
import { fetchAllCourses } from '../../../api/apiServices';

const levelColorMap = {
  Beginner: 'teal',
  Intermediate: 'amber',
  Advanced: 'rose',
};

export default function FeaturedCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchAllCourses();

        if (response.success && Array.isArray(response.courses)) {
          // Only show published courses
          const publishedCourses = response.courses.filter(course => course.isPublished === true);
          setCourses(publishedCourses);
        } else {
          setError(response.message || "Failed to load courses");
        }
      } catch (err) {
        console.error("Failed to fetch courses:", err);
        setError("Failed to load courses. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  const featured = courses.slice(0, 3);

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500">Loading featured courses...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || featured.length === 0) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-500">{error || "No courses available at the moment"}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="text-teal-600 text-sm font-semibold uppercase tracking-widest mb-2">Featured Picks</p>
            <h2 className="text-4xl font-bold text-slate-900 leading-tight">
              Discover Our Best Courses
            </h2>
            <p className="text-slate-600 mt-3 max-w-md">
              Handpicked courses from expert instructors to accelerate your learning
            </p>
          </div>

          <Link 
            to="/courses" 
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold group"
          >
            Browse All Courses
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </Link>
        </div>

        {/* Featured Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((course) => (
            <Link 
              key={course._id || course.id} 
              to={`/courses/${course._id || course.id}`}
              className="group bg-white rounded-3xl overflow-hidden border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 flex flex-col h-full"
            >
              {/* Image Section */}
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={course.thumbnail} 
                  alt={course.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                />
                
                {/* Level Badge */}
                <div className="absolute top-4 left-4">
                  <Badge 
                    label={course.level} 
                    variant={levelColorMap[course.level] || 'slate'} 
                    size="sm"
                  />
                </div>

                {/* Duration */}
                <div className="absolute top-4 right-4 bg-black/70 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1 backdrop-blur-sm">
                  <i className="ri-time-line" />
                  {course.duration}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 flex flex-col">
                {/* Instructor */}
                <div className="flex items-center gap-3 mb-4">
                  {course.instructorAvatar && (
                    <img 
                      src={course.instructorAvatar} 
                      alt={course.instructor} 
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-white" 
                    />
                  )}
                  <div>
                    <p className="text-sm font-medium text-slate-700">{course.instructor}</p>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-slate-900 leading-tight mb-3 line-clamp-2 group-hover:text-teal-600 transition-colors">
                  {course.title}
                </h3>

                {/* Description */}
                <p className="text-slate-600 text-sm line-clamp-3 mb-6 flex-1">
                  {course.description}
                </p>

                {/* Bottom Section */}
                <div className="pt-6 border-t border-slate-100 mt-auto">
                  <div className="flex items-center justify-between">
                    <div>
                      {!course.isPaid || !course.price || course.price === 0 ? (
                        <span className="text-xl font-extrabold text-emerald-600">Free</span>
                      ) : course.hasDiscount && course.discount > 0 ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl font-extrabold text-slate-900">
                            ${(course.price * (1 - (course.discount / 100))).toFixed(2)}
                          </span>
                          <span className="text-xs text-slate-400 line-through">
                            ${Number(course.price).toFixed(2)}
                          </span>
                          <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">
                            {course.discount}% OFF
                          </span>
                        </div>
                      ) : (
                        <span className="text-xl font-extrabold text-slate-900">${course.price}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                      Enroll Now 
                      <span className="text-base">→</span>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-1 mt-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i 
                        key={star}
                        className={`ri-star-${star <= Math.floor(course.rating || 4.5) ? 'fill' : 'line'} text-amber-400 text-sm`} 
                      />
                    ))}
                    <span className="ml-2 text-sm text-slate-500">
                      ({course.rating || 4.5})
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile "View All" Button */}
        <div className="text-center mt-12 md:hidden">
          <Link 
            to="/courses" 
            className="inline-flex items-center gap-2 px-8 py-3 bg-slate-900 text-white rounded-2xl font-semibold hover:bg-slate-800 transition"
          >
            View All Courses
          </Link>
        </div>
      </div>
    </section>
  );
}